// Everything the config dialog writes, in three entry points:
//
//   buildCreateWrites   a new resource and its first schedule
//   buildUpdateWrites   an edited resource; the schedule only if it changed
//   buildDeleteWrites   ending a resource's schedule (the whole resource is its own mutation)
//
// Each returns the payload saveResource takes:
//
//   { resource, rules, overrides, regions, shipping_partners }
//
// where each list is `{ add, update, remove }`. `{}` means "leave it alone", so a rename writes no
// rule or override at all.
//
// The shared pieces below are the only places a rule is ended or an override released, so
// create, update and delete can't disagree about how that works.

import { notEnded } from '@/utilities/resourceDays';
import { UPDATE_TYPES } from '../../constants';
import { coreDay } from '../../utils';
import {
    SCHEDULE_TYPES,
    earliest,
    keyOf,
    oneOffRows,
    recurrenceKey,
    rulesForSchedule,
    sameSchedule,
    scheduledRules,
    upcomingOneOffs,
} from './schedule';

const LEAVE_ALONE = Object.freeze({});

const byMappingId = ({ mapping_id }) => ({ mapping_id });

// The row itself, without the relationships the fragment nests on it.
export const resourceRow = ({ __typename, overrides, rules, subregions, shipping_partners, ...row } = {}) => row;

// ---- Shared pieces ---------------------------------------------------------------------------

// resource_override_mappings.rule_id has a NO ACTION foreign key, so a rule can only be deleted in
// the same write that clears every override pointing at it — a lock on any one of them otherwise
// rolls the whole save back. Past overrides are the record of days already run, so they are kept,
// detached (rule_id null) rather than deleted; today's and later ones go with the schedule.
//
// The detach rides UPSERT_RESOURCES' override upsert, which runs before removed_rules in the same
// transaction. Nothing reads rule_id to decide a day — scheduleResource and resourceDayState match
// on the window and `omit` alone — so a detached override keeps its day exactly as it was.
//
// `rule.overrides` rather than the resource's own list: that one is cut off a month back, the
// relationship under each rule is not, and the FK counts every row. The store keeps both in step
// when a lock is saved (syncRuleOverrides).
export const releaseOverrides = (rules = [], todayStart) => {
    const asOf = new Date(todayStart);
    const overrides = rules.flatMap((rule) => rule.overrides || []);
    const isPast = (override) => new Date(override.override_end) <= asOf;

    return {
        update: overrides.filter(isPast).map(({ mapping_id, resource_id, override_start, override_end, omit }) => ({
            mapping_id,
            resource_id,
            override_start,
            override_end,
            omit,
            rule_id: null,
        })),
        remove: overrides.filter((override) => !isPast(override)).map(byMappingId),
    };
};

// A rule ended AT `asOf`, not the day before: scheduleResource's overlaps() counts a rule as
// covering a day when the rule's end matches the day's end, so rule_end = asOf stops at asOf while
// still covering the day before it. Explicit fields, since the fragment carries __typename and a
// nested overrides list that don't belong in the input.
const endedAt = (rule, asOf) => ({
    mapping_id: rule.mapping_id,
    resource_id: rule.resource_id,
    rule_start: rule.rule_start,
    rule_type: rule.rule_type,
    rule_end: asOf,
});

// Takes the resource's current rules off the schedule, from the `split` day on.
//
//   OVERWRITE  deletes every rule — the schedule is rewritten for every day, past included.
//   SPLIT      ends each rule already running at the split day's start, and deletes the ones that
//              start on `upcomingFrom` or later, since a rule can't end before its own start.
//              `keep` spares a later rule the new schedule would only write again, so there's no
//              churn.
//
// `upcomingFrom` is the split day unless the caller knows better — see rescheduleWrites.
//
// "Already running" is decided by date, not instant. Rules start at UTC midnight and the split is a
// business midnight, so an instant comparison saw a rule starting on the split day as six hours old
// and ended it at its own start, leaving the row — and any lock on it — in place.
//
// Returns the rule writes, the overrides the deleted rules release, and the rules kept.
const retireRules = ({ rules, updateType, split, upcomingFrom = split.key, todayStart, keep = () => false }) => {
    if (updateType === UPDATE_TYPES.OVERWRITE) {
        return {
            rules: { update: [], remove: rules.map(byMappingId) },
            overrides: releaseOverrides(rules, todayStart),
            kept: [],
        };
    }

    const isUpcoming = (rule) => keyOf(rule.rule_start) >= upcomingFrom;
    const upcoming = rules.filter(isUpcoming);
    const running = rules.filter((rule) => !isUpcoming(rule) && notEnded(rule, new Date(split.start)));
    const kept = upcoming.filter(keep);
    const dropped = upcoming.filter((rule) => !kept.includes(rule));

    return {
        rules: {
            update: running.map((rule) => endedAt(rule, split.start)),
            remove: dropped.map(byMappingId),
        },
        overrides: releaseOverrides(dropped, todayStart),
        kept,
    };
};

const mergeWrites = (...parts) => ({
    add: parts.flatMap((part) => part.add || []),
    update: parts.flatMap((part) => part.update || []),
    remove: parts.flatMap((part) => part.remove || []),
});

// Subregion mappings: selected ones added or kept, deselected ones removed.
export const regionWrites = ({ resource, regions, subregions }) => {
    const selected = regions.map(([id]) => id);
    const existing = resource?.subregions || [];

    return {
        add: selected
            .filter((id) => !existing.some((mapping) => mapping.subregion_id === id))
            .map((id) => subregions[id]),
        update: existing
            .filter((mapping) => selected.includes(mapping.subregion_id))
            .map(({ __typename, ...mapping }) => mapping),
        remove: existing
            .filter((mapping) => !selected.includes(mapping.subregion_id))
            .map(({ __typename, ...mapping }) => mapping),
    };
};

// Shipping-partner mappings. A resource open to every shipper carries one partner_client_id: null
// row, enabled exactly when no partner is picked; picked partners get their own enabled rows and
// unpicked ones are disabled rather than deleted.
export const partnerWrites = ({ resource, shippingPartners }) => {
    const resourceId = resource?.resource_id;
    const saved = (resource?.shipping_partners || [])
        .filter((mapping) => mapping.partner_client_id !== null)
        .map((mapping) => mapping.partner_client.client_id);

    const openToAll = { partner_client_id: null, enabled: shippingPartners.length === 0 };

    return {
        add: [
            ...(resourceId ? [] : [openToAll]),
            ...shippingPartners
                .filter((id) => !resourceId || !saved.includes(id))
                .map((id) => ({ partner_client_id: id, enabled: true })),
        ],
        update: saved.map((id) => ({
            resource_id: resourceId,
            partner_client_id: id,
            enabled: shippingPartners.includes(id),
        })),
        globals: resourceId ? [{ resource_id: resourceId, ...openToAll }] : [],
    };
};

// ---- The schedule ----------------------------------------------------------------------------

// Where "from the start date on" switches over: the schedule's start date, or today once that has
// passed. `start` is that day's business midnight, the bound locks and rule ends are written at.
//
// A one-off schedule switches over today. Its start date is only its first date, which the
// operator never chose as a switch-over — splitting there kept the old schedule running until then.
export const splitDay = ({ schedule, today, tz }) =>
    schedule.type !== SCHEDULE_TYPES.ONE_OFF && schedule.startKey > today.key
        ? { key: schedule.startKey, start: coreDay(schedule.startKey, tz).start }
        : today;

// A start date moved into the past rewrites days already run, which only the whole-schedule
// option does.
export const onlyOverwrite = ({ schedule, saved, today }) =>
    schedule.startKey < today.key && schedule.startKey !== saved.startKey;

// Whether saving has to ask which days the change covers: when rules already scheduled would be
// retired, or when the start date moved into the past. The second holds with nothing scheduled
// too — without the question, the save went out as "from the start date on", which starts today,
// and the past date the operator entered was dropped without a word.
//
// A first schedule, or one-off dates moved to other one-off dates, has nothing to ask about.
export const needsScope = ({ resource, schedule, saved, today }) =>
    !sameSchedule(schedule, saved) &&
    (scheduledRules(resource, today.start).length > 0 || onlyOverwrite({ schedule, saved, today }));

// Where the whole-schedule option starts the new rules. It deletes every rule the resource has, but
// the start date the dialog opens on only counts the ones still running in the form's type — so a
// rule that ended last month, or the monthly half of a mixed schedule, can start earlier, and
// starting at the dialog's date dropped the resource from those days. An untouched start date
// reaches back to the earliest rule; one the operator moved is theirs.
const overwriteStartKey = ({ resource, schedule, saved }) =>
    schedule.startKey === saved.startKey
        ? earliest([schedule.startKey, ...(resource?.rules || []).map((rule) => keyOf(rule.rule_start))])
        : schedule.startKey;

// The rule and override writes that swap the saved schedule for `schedule`.
const rescheduleWrites = ({ resource, schedule, saved, updateType, today, tz }) => {
    const split = splitDay({ schedule, today, tz });
    // "From the start date on" starts the new schedule on the split day; only the whole-schedule
    // option starts it on a day already behind us, since that rewrites the days in between.
    const fresh = rulesForSchedule(
        schedule,
        updateType === UPDATE_TYPES.OVERWRITE ? overwriteStartKey({ resource, schedule, saved }) : split.key
    );
    // Recurrence -> the day the new schedule would start it. A rule already waiting to start is
    // kept only if it starts that same day; one starting later would drop the weeks in between.
    const freshStarts = new Map(fresh.map((rule) => [recurrenceKey(rule), keyOf(rule.rule_start)]));

    // One-off dates edited in place: add the new dates, drop the ones taken away. Days already run
    // stay as they were.
    if (schedule.type === SCHEDULE_TYPES.ONE_OFF && saved.type === SCHEDULE_TYPES.ONE_OFF) {
        const existing = upcomingOneOffs(resource, today.start);
        const existingKeys = existing.map((o) => keyOf(o.override_start));

        return {
            rules: LEAVE_ALONE,
            overrides: {
                add: oneOffRows(
                    schedule.dates.filter((key) => !existingKeys.includes(key)),
                    tz
                ),
                update: [],
                remove: existing.filter((o) => !schedule.dates.includes(keyOf(o.override_start))).map(byMappingId),
            },
        };
    }

    const scheduled = scheduledRules(resource, today.start);

    // A schedule that hasn't run a day yet has no past to keep, so every rule it has is retired
    // outright rather than ended at the split day. Ending them there left a start date pushed from
    // Oct 1 to Oct 15 running Oct 1–14 on the old rules.
    const hasRun = scheduled.some((rule) => keyOf(rule.rule_start) < today.key);

    const retired = retireRules({
        // OVERWRITE rewrites every day, so rules that already ended go too.
        rules: updateType === UPDATE_TYPES.OVERWRITE ? resource?.rules || [] : scheduled,
        updateType,
        split,
        upcomingFrom: hasRun ? split.key : today.key,
        todayStart: today.start,
        keep: (rule) => freshStarts.get(recurrenceKey(rule)) === keyOf(rule.rule_start),
    });

    const keptKeys = new Set(retired.kept.map(recurrenceKey));

    // Leaving a one-off schedule: its dates still to come go, the rules replace them.
    const leftOneOffs =
        saved.type === SCHEDULE_TYPES.ONE_OFF
            ? { remove: upcomingOneOffs(resource, today.start).map(byMappingId) }
            : {};

    return {
        rules: {
            add: fresh.filter((rule) => !keptKeys.has(recurrenceKey(rule))),
            ...retired.rules,
        },
        overrides: mergeWrites(
            retired.overrides,
            leftOneOffs,
            schedule.type === SCHEDULE_TYPES.ONE_OFF ? { add: oneOffRows(schedule.dates, tz) } : {}
        ),
    };
};

// ---- Entry points ----------------------------------------------------------------------------

export const buildCreateWrites = ({ form, schedule, regions, subregions, shippingPartners, tz }) => ({
    resource: resourceRow(form),
    rules: { add: rulesForSchedule(schedule) },
    overrides: { add: schedule.type === SCHEDULE_TYPES.ONE_OFF ? oneOffRows(schedule.dates, tz) : [] },
    regions: regionWrites({ resource: null, regions, subregions }),
    shipping_partners: partnerWrites({ resource: null, shippingPartners }),
});

// `saved` is the schedule the dialog opened on. An untouched schedule writes no rule or override —
// renaming a resource must never split its rule and take its locks with it.
export const buildUpdateWrites = ({
    resource,
    form,
    schedule,
    saved,
    updateType,
    today,
    tz,
    regions,
    subregions,
    shippingPartners,
}) => ({
    resource: resourceRow(form),
    ...(sameSchedule(schedule, saved)
        ? { rules: LEAVE_ALONE, overrides: LEAVE_ALONE }
        : rescheduleWrites({ resource, schedule, saved, updateType, today, tz })),
    regions: regionWrites({ resource, regions, subregions }),
    shipping_partners: partnerWrites({ resource, shippingPartners }),
});

// The saved row, not the form, and no region or partner writes: deleting must not quietly persist
// whatever else was edited before Delete was pressed.
//
//   SPLIT      the schedule ends today: live rules end, later ones and upcoming one-offs go
//   OVERWRITE  every rule and every one-off day goes, past included
export const buildDeleteWrites = ({ resource, updateType, today }) => {
    const allRules = resource?.rules || [];
    const isOverwrite = updateType === UPDATE_TYPES.OVERWRITE;

    const retired = retireRules({
        rules: isOverwrite ? allRules : scheduledRules(resource, today.start),
        updateType,
        split: today,
        todayStart: today.start,
    });

    const oneOffs = isOverwrite
        ? (resource?.overrides || []).filter((o) => o.omit === false && !o.rule_id)
        : upcomingOneOffs(resource, today.start);

    return {
        resource: resourceRow(resource),
        rules: { add: [], ...retired.rules },
        overrides: mergeWrites(retired.overrides, { remove: oneOffs.map(byMappingId) }),
        regions: LEAVE_ALONE,
        shipping_partners: LEAVE_ALONE,
    };
};

// Whether there's a schedule for Delete to end, rather than only the whole resource to remove.
export const hasSchedule = (resource, today) =>
    scheduledRules(resource, today.start).length > 0 || upcomingOneOffs(resource, today.start).length > 0;
